// frontend/src/components/chat/ChatWindow.jsx
import React, { useState, useRef, useEffect, useMemo, useCallback } from 'react';
import {
  Box, Typography, IconButton, TextField, Avatar, Paper,
  InputAdornment, CircularProgress, Tooltip, Chip, Alert
} from '@mui/material';
import {
  Close as CloseIcon, Send as SendIcon, EmojiEmotions as EmojiIcon,
  Check as CheckIcon, DoneAll as DoneAllIcon, Minimize as MinimizeIcon, Expand as ExpandIcon
} from '@mui/icons-material';
import EmojiPicker from 'emoji-picker-react';
import { useChat } from '../../context/ChatContext';
import { useAuth } from '../../context/AuthContext';

const THEME = {
  primary: '#667eea', primaryLight: 'rgba(102, 126, 238, 0.12)',
  primaryGradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  textPrimary: '#1e293b', textSecondary: '#64748b', textDisabled: '#94a3b8',
  border: '#e2e8f0', background: '#f8fafc', surface: '#ffffff',
  messageMe: '#667eea', messageMeText: '#ffffff', messageOther: '#ffffff',
  messageOtherText: '#1e293b', online: '#22c55e', offline: '#94a3b8',
};

const formatTime = (ts) => ts ? new Date(ts).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '';
const formatDate = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  const today = new Date();
  const yesterday = new Date(today); yesterday.setDate(yesterday.getDate() - 1);
  if (d.toDateString() === today.toDateString()) return 'Today';
  if (d.toDateString() === yesterday.toDateString()) return 'Yesterday';
  return d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};
const getAvatarUrl = (av) => {
  if (!av) return null;
  const base = import.meta.env?.VITE_AVATAR_URL || process.env?.REACT_APP_AVATAR_URL || 'http://localhost:8000/storage';
  return `${base.replace(/\/+$/, '')}/${av.replace(/^\/+/, '')}`;
};

const MessageBubble = React.memo(({ msg, isMe }) => {
  return (
    <Box sx={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', alignItems: 'flex-end', gap: 0.75, mb: 0.5 }}>
      {!isMe && (
        <Avatar src={getAvatarUrl(msg.user?.avatar)} sx={{ width: 28, height: 28, fontSize: '0.75rem', bgcolor: THEME.primary, border: '2px solid white', flexShrink: 0 }}>
          {!getAvatarUrl(msg.user?.avatar) && (msg.user?.name?.charAt(0)?.toUpperCase() || '')}
        </Avatar>
      )}
      <Box sx={{ maxWidth: '75%' }}>
        {!isMe && msg.user?.name && <Typography variant="caption" sx={{ ml: 0.5, color: THEME.textSecondary, fontSize: '0.7rem' }}>{msg.user.name}</Typography>}
        <Box sx={{ px: 1.75, py: 1, borderRadius: isMe ? '18px 18px 4px 18px' : '18px 18px 18px 4px', bgcolor: isMe ? THEME.messageMe : THEME.messageOther, color: isMe ? THEME.messageMeText : THEME.messageOtherText, boxShadow: isMe ? '0 2px 8px rgba(102, 126, 238, 0.25)' : '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
          {msg.body && <Typography variant="body2" sx={{ wordBreak: 'break-word', whiteSpace: 'pre-wrap' }}>{msg.body}</Typography>}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.35, mt: 0.25, justifyContent: isMe ? 'flex-end' : 'flex-start' }}>
            <Typography variant="caption" sx={{ opacity: isMe ? 0.9 : 0.7, fontSize: '0.65rem' }}>{formatTime(msg.created_at)}</Typography>
            {isMe && msg.is_read && <DoneAllIcon sx={{ fontSize: 14 }} />}
            {isMe && !msg.is_read && <CheckIcon sx={{ fontSize: 14, opacity: 0.8 }} />}
          </Box>
        </Box>
      </Box>
    </Box>
  );
});

// ✅ Updated: Props based component
const ChatWindow = ({ conversationId, isMinimized, onClose, onMinimize, onFocus, zIndex }) => {
  const { messages, sendMessage, loadMessages, conversations, onlineUsersData } = useChat();
  const { user: authUser } = useAuth();

  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);

  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const pickerRef = useRef(null);

  // Load messages when conversationId changes
  useEffect(() => {
    if (conversationId) loadMessages(conversationId);
  }, [conversationId]);

  // Auto-scroll
  useEffect(() => {
    if (messagesEndRef.current) messagesEndRef.current.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }, [messages[conversationId]?.length, conversationId]);

  // Focus input on open/expand
  useEffect(() => {
    if (!isMinimized && inputRef.current) setTimeout(() => inputRef.current?.focus(), 150);
  }, [isMinimized]);

  // Close picker on outside click
  useEffect(() => {
    if (!showEmojiPicker) return;
    const handleClickOutside = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) setShowEmojiPicker(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showEmojiPicker]);

  // Data resolution
  const conversation = useMemo(() => conversations?.find(c => c.id === conversationId), [conversations, conversationId]);
  
  const otherUser = useMemo(() => {
    if (!conversation) return null;
    let baseUser = conversation.other_users?.[0] || conversation.users?.find(u => String(u.id) !== String(authUser?.id)) || conversation.users?.[0];
    if (!baseUser) return null;
    const livePresence = onlineUsersData?.find(u => String(u.id) === String(baseUser.id));
    return { ...baseUser, is_online: livePresence?.is_online ?? baseUser.is_online ?? false };
  }, [conversation, authUser?.id, onlineUsersData]);

  const chatTitle = otherUser?.name || conversation?.name || 'Chat';
  const displayAvatar = getAvatarUrl(otherUser?.avatar);
  const isOnline = otherUser?.is_online === true;

  const convMessages = useMemo(() => {
    const raw = messages[conversationId] || [];
    return [...raw].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  }, [messages, conversationId]);

  const messagesWithDates = useMemo(() => {
    const result = [];
    let lastDate = null;
    convMessages.forEach((msg) => {
      const d = new Date(msg.created_at).toDateString();
      if (d !== lastDate) { result.push({ type: 'date', date: msg.created_at, id: `date-${d}` }); lastDate = d; }
      result.push({ type: 'message', ...msg });
    });
    return result;
  }, [convMessages]);

  const handleSend = useCallback(async () => {
    if (!input.trim() || !conversationId || typeof sendMessage !== 'function') return;
    const text = input.trim();
    setInput(''); setSending(true); setError('');
    try { await sendMessage(conversationId, text); }
    catch (err) { setError(err.message || 'Failed to send.'); setInput(text); }
    finally { setSending(false); }
  }, [input, conversationId, sendMessage]);

  const handleKeyDown = (e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend(); } };

  const handleEmojiClick = (emojiObject) => {
    setInput(prev => prev + emojiObject.emoji);
    setShowEmojiPicker(false);
    setTimeout(() => inputRef.current?.focus(), 50);
  };

  return (
    <Paper 
      onClick={onFocus} // Bring to front on click
      elevation={8} 
      sx={{
        position: 'fixed', bottom: 24, right: { xs: 0, sm: 24 }, left: { xs: 0, sm: 'auto' }, top: { xs: 0, sm: 'auto' },
        width: { xs: '100vw', sm: 380 },
        height: isMinimized ? 'auto' : { xs: '100svh', sm: 520 },
        maxHeight: isMinimized ? 'none' : '85vh',
        borderRadius: { xs: 0, sm: 3 },
        boxShadow: { xs: 'none', sm: isMinimized ? '0 8px 24px rgba(0,0,0,0.12)' : '0 20px 40px rgba(0, 0, 0, 0.15)' },
        border: { xs: 'none', sm: `1px solid ${THEME.border}` },
        zIndex: zIndex, // ✅ Controlled z-index
        display: 'flex', flexDirection: 'column', overflow: 'hidden',
        transition: 'height 0.3s ease, max-height 0.3s ease, box-shadow 0.3s ease'
      }}
    >
      {/* Header */}
      <Box sx={{ background: THEME.primaryGradient, color: 'white', p: 1.5, display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0 }}>
        <Box sx={{ position: 'relative' }}>
          <Avatar src={displayAvatar} sx={{ width: 40, height: 40, bgcolor: THEME.secondary, border: '2px solid white' }}>
            {!displayAvatar && chatTitle?.charAt(0)?.toUpperCase()}
          </Avatar>
          <Box sx={{ position: 'absolute', bottom: 2, right: 2, width: 12, height: 12, bgcolor: isOnline ? THEME.online : THEME.offline, borderRadius: '50%', border: '2px solid white' }} />
        </Box>
        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
          <Typography variant="subtitle2" fontWeight={700} noWrap>{chatTitle}</Typography>
          <Typography variant="caption" sx={{ opacity: 0.9 }}>{isOnline ? 'Online' : 'Offline'}</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 0.5 }}>
          <Tooltip title={isMinimized ? "Expand" : "Minimize"}>
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onMinimize(); }} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
              {isMinimized ? <ExpandIcon /> : <MinimizeIcon />}
            </IconButton>
          </Tooltip>
          <Tooltip title="Close">
            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onClose(); }} sx={{ color: 'white', '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' } }}>
              <CloseIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ maxHeight: isMinimized ? 0 : '100%', overflow: 'hidden', transition: 'max-height 0.3s ease', display: 'flex', flexDirection: 'column', flexGrow: 1, minHeight: 0 }}>
        {error && <Alert severity="error" sx={{ mx: 2, mt: 1 }} onClose={() => setError('')}>{error}</Alert>}
        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1.5, bgcolor: THEME.background }}>
          {convMessages.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}><Typography color="text.secondary">No messages yet. Say hello! 👋</Typography></Box>
          ) : (
            messagesWithDates.map(item => item.type === 'date' ? <Box key={item.id} sx={{ textAlign: 'center', my: 2 }}><Chip label={formatDate(item.date)} size="small" /></Box> : <MessageBubble key={item.id} msg={item} isMe={item.user_id === authUser?.id} />)
          )}
          <div ref={messagesEndRef} />
        </Box>
        
        {/* Input */}
        <Box sx={{ p: 1.5, borderTop: `1px solid ${THEME.border}`, bgcolor: THEME.surface, position: 'relative' }}>
          {showEmojiPicker && (
            <Box ref={pickerRef} sx={{ position: 'absolute', bottom: '100%', left: 0, right: 0, mb: 1, zIndex: 10, bgcolor: THEME.surface, maxHeight: 320, overflow: 'auto', borderRadius: 2, border: `1px solid ${THEME.border}` }}>
              <EmojiPicker theme="light" onEmojiClick={handleEmojiClick} previewConfig={{ showPreview: false }} style={{ width: '100%', '--epr-emoji-variation-picker-bg': THEME.surface }} />
            </Box>
          )}
          <TextField fullWidth size="small" placeholder="Type a message..." value={input} onChange={(e) => setInput(e.target.value)} onKeyDown={handleKeyDown} disabled={sending} multiline maxRows={3} sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2.5, bgcolor: THEME.background } }} InputProps={{ endAdornment: (<InputAdornment position="end"><IconButton size="small" onClick={() => setShowEmojiPicker(!showEmojiPicker)} sx={{ p: 0.5, color: THEME.textDisabled }}><EmojiIcon fontSize="small" /></IconButton><IconButton size="small" onClick={handleSend} disabled={!input.trim() || sending} sx={{ p: 0.5, bgcolor: input.trim() ? THEME.primary : THEME.textDisabled, color: 'white' }}>{sending ? <CircularProgress size={16} /> : <SendIcon fontSize="small" />}</IconButton></InputAdornment>) }} />
        </Box>
      </Box>
    </Paper>
  );
};

export default ChatWindow;